/**
 * Beats — the free-text subjects an account covers.
 *
 * PURE and shared by the client (chip editor) and the server (feed match), so
 * the phrase a user types and the phrase the query matches on are normalized by
 * the same code. Two copies of this would drift on the first apostrophe.
 *
 * A beat is not a category. `discover_articles.category` is a fixed set
 * produced by ingestion; "data centers", "economic obsolescence" and "ERCOT"
 * will never be members of it. What a beat does is match the words that are
 * already in the corpus — 26 data-centre articles landed in 90 days from the
 * general and AI wires without anybody subscribing to them.
 */

/** Matches the CHECK on discover_brand_kit_preferences (migration 20261005180000). */
export const MAX_BEATS = 30;
export const MAX_BEAT_LENGTH = 60;

/**
 * One beat, cleaned. Returns null when nothing usable survives.
 *
 * Punctuation is stripped from the ENDS only — "AI/ML" and "ERCOT's" keep their
 * internal characters, because a beat is a phrase people type naturally, not a
 * slug.
 */
export function normalizeBeat(raw: string): string | null {
  const cleaned = raw
    .replace(/\s+/g, ' ')
    .replace(/^[\s,;."'`]+|[\s,;."'`]+$/g, '')
    .trim();
  if (!cleaned) return null;
  return cleaned.slice(0, MAX_BEAT_LENGTH);
}

/**
 * Clean a whole list: normalize, drop empties, de-duplicate case-insensitively
 * (keeping the FIRST spelling the user chose, so "Data Centers" is not silently
 * rewritten to "data centers"), and cap the count.
 */
export function normalizeBeats(raw: readonly string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const beat = normalizeBeat(item);
    if (!beat) continue;
    const key = beat.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(beat);
    if (out.length >= MAX_BEATS) break;
  }
  return out;
}

/**
 * PostgREST `.or()` filters are a comma-separated list, and the value half is
 * NOT quoted — so a comma, a parenthesis or a dot inside a beat would break out
 * of the filter and change its meaning. `%` and `_` would widen the LIKE.
 *
 * This is a filter-syntax boundary, not cosmetics: "R&D, (Texas)" must never be
 * able to inject a second condition. Stripped rather than escaped because
 * PostgREST has no escape for these inside an `or` group.
 */
export function sanitizeBeatForFilter(beat: string): string {
  return beat
    .replace(/[%_,().*:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * The stem to actually search on — a plural beat must not miss its singular.
 *
 * MEASURED, not assumed. On prod (2026-08-13, 90-day window) `%data center%`
 * matched **16** articles and `%data centers%` matched **9**: because these are
 * substring matches the singular already catches every plural ("data centers"
 * CONTAINS "data center"), while the plural catches none of the singulars. A
 * user typing the natural plural was therefore silently losing 44% of their own
 * beat — the kind of miss nobody can see, because a thinner feed looks exactly
 * like a quiet news day.
 *
 * Only the LAST word is stemmed: "data centers" is a phrase whose head noun is
 * the one that inflects. The user's typed spelling is never changed — this
 * affects matching only.
 */
export function beatMatchStem(beat: string): string {
  const words = beat.trim().split(' ');
  const last = words[words.length - 1] ?? '';
  if (last.length < 4) return beat.trim();

  let stemmed = last;
  if (/[^aeiou]ies$/i.test(last)) {
    stemmed = last.slice(0, -3); // policies -> polic  (matches policy AND policies)
  } else if (/(s|x|z|ch|sh)es$/i.test(last)) {
    stemmed = last.slice(0, -2); // taxes -> tax
  } else if (/[^su]s$/i.test(last)) {
    stemmed = last.slice(0, -1); // centers -> center
  }

  words[words.length - 1] = stemmed;
  return words.join(' ');
}

/**
 * A beat typed in capitals is an acronym — ERCOT, PILOT, BPP, JETI — and the
 * capitals are the signal. Matched case-insensitively as a substring, "PILOT"
 * (payment in lieu of taxes) matched 25 articles in a 30-day window, every one
 * of them about airline pilots or Microsoft Copilot. An acronym matches only
 * itself: whole word, exact case, never stemmed. One token only — "DATA CENTER"
 * typed in caps is a phrase, not an acronym.
 */
export function isAcronymBeat(beat: string): boolean {
  const token = beat.trim();
  return /^[A-Z0-9&/.-]{2,8}$/.test(token) && /[A-Z]/.test(token);
}

/** Beats are typed phrases; regex syntax in one is stripped, never interpreted. */
function regexSafe(text: string): string {
  return sanitizeBeatForFilter(text)
    .replace(/[+?[\]{}|^$\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

interface BeatMatcher {
  /** PostgREST operator: `match` (case-sensitive) or `imatch`. */
  op: 'match' | 'imatch';
  /** Postgres ARE. `\m`/`\M` are its word start/end. */
  sql: string;
  test: (haystack: string) => boolean;
}

/**
 * One rule, two engines: the feed query and the card label must agree on what a
 * beat matches, or a card would arrive and then decline to say why.
 *
 * A word beat matches from the START of a word, so "data center" still finds
 * "data centers" (the stem does the plural) but "pilot" no longer finds
 * "Copilot" and "tax" no longer finds "syntax".
 */
export function beatMatcher(beat: string): BeatMatcher | null {
  if (isAcronymBeat(beat)) {
    const safe = regexSafe(beat.trim());
    if (safe.length < 2) return null;
    const re = new RegExp(`(?<![A-Za-z0-9])${safe}(?![A-Za-z0-9])`);
    return { op: 'match', sql: `\\m${safe}\\M`, test: (h) => re.test(h) };
  }
  const safe = regexSafe(beatMatchStem(beat)).toLowerCase();
  if (safe.length < 2) return null;
  const re = new RegExp(`(?<![a-z0-9])${safe}`, 'i');
  return { op: 'imatch', sql: `\\m${safe}`, test: (h) => re.test(h) };
}

/**
 * The PostgREST `.or(...)` argument matching any of `beats` against title or
 * description. Returns null when nothing survives sanitising — the caller must
 * then apply NO filter rather than an empty one, which would match everything.
 */
export function beatsOrFilter(
  beats: readonly string[],
  columns: readonly string[] = ['title', 'description'],
): string | null {
  const clauses: string[] = [];
  for (const beat of normalizeBeats(beats)) {
    const matcher = beatMatcher(beat);
    if (!matcher) continue;
    for (const column of columns) {
      clauses.push(`${column}.${matcher.op}.${matcher.sql}`);
    }
  }
  return clauses.length > 0 ? clauses.join(',') : null;
}

/**
 * Which beats does this article actually hit? Used to LABEL a card ("Data
 * centers") rather than to filter — the reader should be able to see why an
 * article is in their feed, the same way a board card names its source.
 */
export function matchedBeats(
  article: { title?: string | null; description?: string | null },
  beats: readonly string[],
): string[] {
  const haystack = `${article.title ?? ''} ${article.description ?? ''}`;
  if (!haystack.trim()) return [];
  return normalizeBeats(beats).filter((beat) => beatMatcher(beat)?.test(haystack) ?? false);
}

/**
 * How specific was the strongest beat this article matched? Higher ranks first.
 *
 * MEASURED PROBLEM (prod, 2026-08-13, 90-day window). A tax practice following
 * `data centers, property tax, tax incentives, site selection, depreciation,
 * valuation` got a beat feed dominated by startup funding stories — "Anthropic
 * investors bet on $2tn valuation", "Databricks … $190 billion valuation",
 * "Roger Federer leaves Forbes' billionaires list". The cause is arithmetic:
 *
 *     valuation      34 matches
 *     data center    18
 *     property tax    1
 *     site selection  1
 *
 * One generic single word out-matched the specific multi-word beats nearly 2:1,
 * and pure recency ordering then handed it most of the visible window. The
 * account's most valuable subject was buried under its least valuable one.
 *
 * Word count is a crude proxy for specificity, and it is the right one here: a
 * multi-word beat is a PHRASE somebody chose deliberately ("property tax",
 * "site selection"), while a lone common noun is a word that happens to appear
 * everywhere. Ranking by it puts "Texas data center standards" above "$2tn
 * valuation" WITHOUT discarding either — the generic beat still earns its
 * matches, it just stops leading.
 */
export function beatSpecificity(
  article: { title?: string | null; description?: string | null },
  beats: readonly string[],
): number {
  const hits = matchedBeats(article, beats);
  if (hits.length === 0) return 0;
  const longest = Math.max(...hits.map((b) => b.trim().split(/\s+/).length));
  // A second matched beat is real corroboration — an article hitting both
  // "data center" AND "property tax" is precisely the one this account wants.
  return longest * 10 + hits.length;
}

/**
 * Can a competitor's NAME be searched in news without drowning in strangers?
 * "Merit Advisors", "Deloitte", "KPMG" can; "Ryan" (a first name), "DMA" (the
 * EU Digital Markets Act) and "EY" cannot — matched as words they return
 * mostly unrelated people and laws. Two or more words, a single word of five or
 * more letters, or an acronym of four or more capitals.
 */
export function isSearchableName(name: string): boolean {
  const clean = name.trim();
  if (clean.split(/\s+/).length >= 2) return true;
  if (/^[A-Z0-9&]{4,}$/.test(clean)) return true;
  return /[a-z]/.test(clean) && clean.length >= 5;
}

/**
 * Subject suggestions from the workspace's OWN brand profile: the keywords
 * under each content pillar's branches, heaviest pillar first. ITC Tax's
 * profile already names "Property tax valuation", "ERCOT and large loads",
 * "Site selection" — and Discover offered it accounting-firm generics instead.
 *
 * Only phrases a headline could contain survive: up to four words, no "vs.",
 * no slashes, no "perspective" — "Owner / operator / occupant" and
 * "Former-appraiser perspective" are positioning, not news subjects.
 * Offered, never applied (see BeatsEditor).
 */
export function subjectSuggestionsFromGuidelines(
  guidelines: unknown,
  existing: readonly string[] = [],
  limit = 12,
): string[] {
  const pillars = (guidelines as { pillars?: unknown } | null)?.pillars;
  if (!Array.isArray(pillars)) return [];
  const taken = new Set(existing.map((b) => b.toLowerCase()));
  const ordered = [...pillars].sort(
    (a, b) =>
      (Number((b as { weight?: unknown })?.weight) || 0) -
      (Number((a as { weight?: unknown })?.weight) || 0),
  );
  const out: string[] = [];
  for (const pillar of ordered) {
    const branches = (pillar as { branches?: unknown })?.branches;
    if (!Array.isArray(branches)) continue;
    for (const branch of branches) {
      const keywords = (branch as { keywords?: unknown })?.keywords;
      if (!Array.isArray(keywords)) continue;
      for (const keyword of keywords) {
        if (typeof keyword !== 'string') continue;
        const phrase = normalizeBeat(keyword);
        if (!phrase) continue;
        if (phrase.split(/\s+/).length > 4) continue;
        if (/\bvs\.?\b|\/|perspective/i.test(phrase)) continue;
        const key = phrase.toLowerCase();
        if (taken.has(key)) continue;
        taken.add(key);
        out.push(phrase);
        if (out.length >= limit) return out;
      }
    }
  }
  return out;
}

/**
 * Starter beats offered when an account has none, keyed by the ICP's vertical.
 *
 * These are SUGGESTIONS in a picker, never applied silently — an unasked-for
 * beat would quietly reshape somebody's whole feed. Kept deliberately short:
 * the point is to show what a beat looks like, not to guess a practice's focus.
 */
export const SUGGESTED_BEATS_BY_VERTICAL: Record<string, string[]> = {
  /* Broad enough that any accounting or tax practice recognises itself. These
     are STARTING POINTS a firm narrows — a property-tax specialist will add
     "property tax", an R&D credit shop will add "R&D credits". Seeding one
     firm's niche here would show every other firm somebody else's beat. */
  accounting: [
    'tax legislation',
    'tax credits',
    'compliance deadlines',
    'audit standards',
    'depreciation',
    'valuation',
  ],
  legal: ['regulation', 'compliance', 'litigation', 'legislation'],
  consulting: ['cost reduction', 'valuation', 'capital projects', 'supply chain'],
  real_estate: ['commercial real estate', 'property tax', 'zoning', 'construction costs'],
  healthcare: ['reimbursement', 'compliance', 'patient experience'],
  agency_services: ['ad spend', 'brand strategy', 'creative trends'],
  coaching: ['leadership', 'career change', 'productivity'],
  education: ['enrollment', 'curriculum', 'edtech'],
  saas: ['pricing', 'churn', 'product-led growth'],
  fintech: ['payments', 'lending', 'regulation'],
};
